/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "@app/axios/axiosIns";

export const getAllEnquires = createAsyncThunk(
  "enquire/all",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/enquire", { params });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getEnquire = createAsyncThunk(
  "enquire/get",
  async (enquireId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/enquire/${enquireId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateEnquire = createAsyncThunk(
  "enquire/update",
  async (
    { id, data }: { id: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(`/enquire/${id}`, data);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteEnquire = createAsyncThunk(
  "enquire/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/enquire/${id}`);
      return { id, data: response?.data};
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const pickFilenameFromContentDisposition = (value?: string): string | undefined => {
  if (!value) return undefined;

  // Examples:
  // content-disposition: attachment; filename="inquires_export_123.csv"
  // content-disposition: attachment; filename*=UTF-8''inquires_export_123.csv
  const match =
    value.match(/filename\*=\s*UTF-8''([^;]+)/i) ||
    value.match(/filename\s*=\s*"([^"]+)"/i) ||
    value.match(/filename\s*=\s*([^;]+)/i);

  const raw = match?.[1]?.trim();
  if (!raw) return undefined;

  try {
    return decodeURIComponent(raw.replace(/^["']|["']$/g, ""));
  } catch {
    return raw.replace(/^["']|["']$/g, "");
  }
};

export const exportEnquires = createAsyncThunk(
  "enquire/export/all",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/enquire/export/all", {
        responseType: "blob",
      });

      const headers = response?.headers || {};
      const contentDisposition: string | undefined =
        headers["content-disposition"] || headers["Content-Disposition"];

      return {
        blob: response?.data as Blob,
        filename:
          pickFilenameFromContentDisposition(contentDisposition) ||
          "enquires_export.csv",
        contentType:
          (headers["content-type"] || headers["Content-Type"]) as string | undefined,
      };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);



interface EnquireState {
  isLoading: boolean;
  formLoader: boolean;
  message: any;
  allMessages: any;
  error: any;
}

const initialState: EnquireState = {
  isLoading: false,
  message: null,
  formLoader: false,
  allMessages: [],
  error: null,
};

const EnquireSlice: any = createSlice({
  name: "enquire",
  initialState,
  reducers: { resetState: () => initialState },
  extraReducers: (builder) => {
    builder
      .addCase(getAllEnquires.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllEnquires.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allMessages = action.payload?.data;
      })
      .addCase(getAllEnquires.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getEnquire.pending, (state) => {
        state.isLoading = true;
        state.message = null;
      })
      .addCase(getEnquire.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.data;
      })
      .addCase(getEnquire.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateEnquire.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateEnquire.fulfilled, (state) => {
        state.formLoader = false;
      })
      .addCase(updateEnquire.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload;
      })
      .addCase(deleteEnquire.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(deleteEnquire.fulfilled, (state) => {
        state.formLoader = false;
      })
      .addCase(deleteEnquire.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload;
      })
  },
});

export const { resetState } = EnquireSlice.actions;
export default EnquireSlice.reducer;

